import {
  ReadAdvancedSearchResultsApiArg,
  SearchUserHit,
  User,
} from 'lib-shared-types-api';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { searchApi } from 'apis/search';
import { pubSub } from 'utils/pubSub';

/**
 * Hook to subscribe to member events and update the RTK Query cache. Updates
 * the team members list when member connection, availability, or capacity
 * changes.
 */
export const useMemberEventsCacheSync = (
  searchQueryParams: ReadAdvancedSearchResultsApiArg,
) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Helper function to derive is_working and is_online from presence_status
    const derivePresenceFlags = (presenceStatus: string) => {
      switch (presenceStatus.toLowerCase()) {
        case 'working':
          return { is_working: true, is_online: true };
        case 'away':
          return { is_working: false, is_online: true };
        case 'offline':
        default:
          return { is_working: false, is_online: false };
      }
    };

    // Helper function to update member in cache
    const updateMemberInCache = (
      memberId: string,
      updates: Partial<SearchUserHit>,
    ) => {
      dispatch(
        searchApi.util.updateQueryData(
          'readAdvancedSearchResults',
          searchQueryParams,
          (draft) => {
            // Find the user result in the cached data
            const userResult = draft.data.results.find(
              (result) => result.hits_type === 'user',
            );

            if (userResult && userResult.hits_type === 'user') {
              // Find and update the specific user in the hits array
              const userIndex = userResult.hits.findIndex(
                (hit) => hit.id === memberId,
              );

              if (userIndex !== -1) {
                // Cast to mutable array since we're in an Immer draft context
                const hits = userResult.hits as SearchUserHit[];
                hits[userIndex] = {
                  ...hits[userIndex],
                  ...updates,
                };
              }
            }
          },
        ),
      );
    };

    // Subscribe to member:connection_changed
    const unsubscribeConnection = pubSub.subscribe(
      'member:connection_changed',
      (event) => {
        const presenceFlags = derivePresenceFlags(event.presence_status);
        const updates: Partial<SearchUserHit> = {
          simultaneous_ring: event.simultaneous_ring_on,
          ...presenceFlags,
          is_online: event.connected && presenceFlags.is_online,
          working_media_types: event.available_on_media,
          custom_away_status: event.custom_away_status_id
            ? {
                custom_away_status_id: event.custom_away_status_id,
                status_updated_at: event.changed_at,
              }
            : undefined,
        };
        updateMemberInCache(event.member_id, updates);
      },
    );

    // Subscribe to member:availability_changed
    const unsubscribeAvailability = pubSub.subscribe(
      'member:availability_changed',
      (event) => {
        const presenceFlags = derivePresenceFlags(event.presence_status);
        const updates: Partial<SearchUserHit> = {
          simultaneous_ring: event.simultaneous_ring_on,
          ...presenceFlags,
          working_media_types: event.available_on_media,
          custom_away_status: event.custom_away_status_id
            ? {
                custom_away_status_id: event.custom_away_status_id,
                status_updated_at: event.changed_at,
              }
            : undefined,
        };
        updateMemberInCache(event.member_id, updates);
      },
    );

    // Subscribe to member:capacity_changed
    const unsubscribeCapacity = pubSub.subscribe(
      'member:capacity_changed',
      (event) => {
        const presenceFlags = derivePresenceFlags(event.presence_status);
        const updates: Partial<SearchUserHit> = {
          simultaneous_ring: event.simultaneous_ring_on,
          ...presenceFlags,
          working_media_types: event.available_on_media,
          workloads_by_channel: event.workload as Record<string, number>,
          custom_away_status: event.custom_away_status_id
            ? {
                custom_away_status_id: event.custom_away_status_id,
                status_updated_at: event.changed_at,
              }
            : undefined,
        };
        updateMemberInCache(event.member_id, updates);
      },
    );

    // Subscribe to userModalUserUpdated (client-side event when user is edited in modal)
    const unsubscribeUserModal = pubSub.subscribe(
      'userModalUserUpdated',
      (updatedUser: User) => {
        const updates: Partial<SearchUserHit> = {
          ...(updatedUser.name != null && { display_name: updatedUser.name }),
          ...(updatedUser.given_name != null && {
            given_name: updatedUser.given_name,
          }),
          ...(updatedUser.family_name != null && {
            family_name: updatedUser.family_name,
          }),
          ...(updatedUser.email != null && { email: updatedUser.email }),
          ...(updatedUser.phone_number != null && {
            phone_number: updatedUser.phone_number,
          }),
          ...(updatedUser.avatar_url != null && {
            avatar_url: updatedUser.avatar_url,
          }),
          ...(updatedUser.additional_emails != null && {
            additional_emails: updatedUser.additional_emails,
          }),
          ...(updatedUser.additional_phone_numbers != null && {
            additional_phone_numbers: updatedUser.additional_phone_numbers,
          }),
        };
        updateMemberInCache(updatedUser.id, updates);
      },
    );

    return () => {
      unsubscribeConnection();
      unsubscribeAvailability();
      unsubscribeCapacity();
      unsubscribeUserModal();
    };
  }, [dispatch, searchQueryParams]);
};
